// src/components/Dashboard/DashboardWidgets.js
// Pinnable widget grid — shown at the top of the dashboard.
// Pin preferences are persisted to localStorage under the key `sk_pinned_widgets`.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  Collapse,
  Button,
  CircularProgress,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import SavingsIcon from '@mui/icons-material/Savings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

import { useNavigate } from 'react-router-dom';
import { useBudget } from '../../contexts/BudgetContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import api from '../../services/api';

// ─── Constants ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'sk_pinned_widgets';

// Default: all 4 widgets pinned
const ALL_WIDGET_IDS = ['spending_today', 'budget_health', 'upcoming_bills', 'savings_progress'];

const DEFAULT_PINNED = [...ALL_WIDGET_IDS];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns true if two Date objects share the same calendar day (local time). */
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// ─── Budget-health status colour & icon ──────────────────────────────────────
const budgetStatusMeta = (pct) => {
  if (pct === null || pct === undefined) return { color: '#9ca3af', icon: null, label: 'N/A' };
  if (pct <= 60) return { color: '#10b981', icon: <CheckCircleIcon fontSize="small" />, label: 'On Track' };
  if (pct <= 85) return { color: '#f59e0b', icon: <WarningIcon fontSize="small" />, label: 'Watch Out' };
  return { color: '#ef4444', icon: <ErrorIcon fontSize="small" />, label: 'Over Budget' };
};

// ─── Circular SVG gauge for the hero tile ────────────────────────────────────
const CircularGauge = ({ value, color, size = 140, stroke = 11 }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safe = Math.max(0, Math.min(100, value));
  const offset = circumference - (safe / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`gauge-${color.replace('#', '')}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.65" />
        </linearGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="rgba(148,163,184,0.18)"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={`url(#gauge-${color.replace('#', '')})`}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.4, 0, 0.2, 1)' }}
      />
    </svg>
  );
};

// ─── Individual Widget Cards ──────────────────────────────────────────────────

/** Shared card shell with pin button. Supports mobile variants: 'hero' | 'compact' | 'wide' | undefined */
const WidgetCard = ({ id, title, icon, color, pinned, onTogglePin, children, loading, mobileVariant }) => {
  const { themeMode } = useThemeContext();
  const isLight = themeMode === 'light';

  const isHero = mobileVariant === 'hero';
  const isCompact = mobileVariant === 'compact';
  const isWide = mobileVariant === 'wide';
  const isVariant = isHero || isCompact || isWide;

  // Hex → rgba helper for gradient tint
  const tint = (alpha) => {
    const m = color.match(/^#([0-9a-f]{6})$/i);
    if (!m) return color;
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        minHeight: isHero ? 210 : isCompact ? 132 : isWide ? 116 : 130,
        borderRadius: isVariant ? 4 : 3,
        position: 'relative',
        overflow: 'hidden',
        background: isVariant
          ? isLight
            ? `linear-gradient(135deg, ${tint(0.10)} 0%, rgba(255,255,255,0.95) 65%)`
            : `linear-gradient(135deg, ${tint(0.18)} 0%, rgba(20,25,40,0.85) 65%)`
          : isLight ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.06)',
        backdropFilter: 'blur(12px)',
        border: isLight ? `1px solid ${tint(0.25)}` : `1px solid ${tint(0.35)}`,
        boxShadow: isLight
          ? `0 4px 16px ${tint(0.12)}`
          : `0 4px 18px rgba(0,0,0,0.35)`,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isLight
            ? `0 6px 22px ${tint(0.20)}`
            : `0 6px 22px rgba(0,0,0,0.5)`,
        },
        // Decorative accent blob
        '&::before': isVariant ? {
          content: '""',
          position: 'absolute',
          top: isHero ? -40 : -24,
          right: isHero ? -40 : -24,
          width: isHero ? 160 : 90,
          height: isHero ? 160 : 90,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${tint(0.18)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        } : {},
      }}
    >
      <CardContent
        sx={{
          p: isHero ? 2.5 : 2,
          '&:last-child': { pb: isHero ? 2.5 : 2 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isHero ? 1.5 : 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {isVariant ? (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: tint(isLight ? 0.15 : 0.25),
                  color,
                }}
              >
                {icon}
              </Box>
            ) : (
              <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: isLight ? 'rgba(15,23,42,0.65)' : 'rgba(248,250,252,0.6)',
                fontSize: '0.68rem',
              }}
            >
              {title}
            </Typography>
          </Box>
          <Tooltip title={pinned ? 'Unpin widget' : 'Pin widget'} placement="top">
            <IconButton
              size="small"
              onClick={() => onTogglePin(id)}
              aria-label={pinned ? `Unpin ${title}` : `Pin ${title}`}
              sx={{ opacity: 0.45, p: 0.5, '&:hover': { opacity: 1 } }}
            >
              {pinned ? (
                <PushPinIcon sx={{ fontSize: 16 }} />
              ) : (
                <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: isHero ? 'center' : 'flex-start' }}>
          {loading ? (
            <Box sx={{ pt: 0.5 }}>
              <Skeleton width="60%" height={isHero ? 60 : 32} />
              <Skeleton width="40%" height={18} sx={{ mt: 0.5 }} />
            </Box>
          ) : (
            children
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ── 1. Spending Today ─────────────────────────────────────────────────────────
const SpendingTodayWidget = ({ pinned, onTogglePin, mobileVariant }) => {
  const { transactions, loading } = useBudget();
  const { formatAmount } = useCurrency();

  const todayTotal = useMemo(() => {
    const today = new Date();
    return transactions
      .filter((t) => {
        if (t.type !== 'expense') return false;
        const d = new Date(t.date);
        return !isNaN(d.getTime()) && isSameDay(d, today);
      })
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  }, [transactions]);

  const txCount = useMemo(() => {
    const today = new Date();
    return transactions.filter((t) => {
      if (t.type !== 'expense') return false;
      const d = new Date(t.date);
      return !isNaN(d.getTime()) && isSameDay(d, today);
    }).length;
  }, [transactions]);

  const isCompact = mobileVariant === 'compact';

  return (
    <WidgetCard
      id="spending_today"
      title="Today"
      icon={<TrendingDownIcon sx={{ fontSize: 18 }} />}
      color="#ef4444"
      pinned={pinned}
      onTogglePin={onTogglePin}
      loading={loading}
      mobileVariant={mobileVariant}
    >
      <Typography
        variant={isCompact ? 'h4' : 'h5'}
        fontWeight={800}
        sx={{
          color: todayTotal > 0 ? '#ef4444' : 'text.primary',
          lineHeight: 1.1,
          fontSize: isCompact ? '1.6rem' : undefined,
          letterSpacing: '-0.02em',
        }}
      >
        {formatAmount(todayTotal)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {txCount === 0
          ? 'No expenses yet'
          : `${txCount} transaction${txCount === 1 ? '' : 's'}`}
      </Typography>
    </WidgetCard>
  );
};

// ── 2. Budget Health ──────────────────────────────────────────────────────────
const BudgetHealthWidget = ({ pinned, onTogglePin, mobileVariant }) => {
  const { totalExpenses, monthlyBudget, loading } = useBudget();
  const { formatAmount } = useCurrency();

  const pct = useMemo(() => {
    if (!monthlyBudget || monthlyBudget <= 0) return null;
    return Math.min(Math.round((totalExpenses / monthlyBudget) * 100), 999);
  }, [totalExpenses, monthlyBudget]);

  const { color, icon, label } = budgetStatusMeta(pct);
  const displayPct = pct !== null ? Math.min(pct, 100) : 0;
  const isHero = mobileVariant === 'hero';

  return (
    <WidgetCard
      id="budget_health"
      title="Budget Health"
      icon={<HealthAndSafetyIcon sx={{ fontSize: 18 }} />}
      color={color}
      pinned={pinned}
      onTogglePin={onTogglePin}
      loading={loading}
      mobileVariant={mobileVariant}
    >
      {isHero ? (
        // ── Hero variant: big circular gauge with embedded percentage ──
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
          <Box sx={{ position: 'relative', width: 130, height: 130, flexShrink: 0 }}>
            <CircularGauge value={displayPct} color={color} size={130} stroke={11} />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.85rem', fontWeight: 800, lineHeight: 1, color, letterSpacing: '-0.03em' }}>
                {pct !== null ? pct : '—'}
                {pct !== null && <Box component="span" sx={{ fontSize: '0.9rem', ml: 0.25 }}>%</Box>}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.25, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                used
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {icon && <Box sx={{ color, display: 'flex' }}>{icon}</Box>}
              <Typography variant="body2" fontWeight={700} sx={{ color }}>
                {pct !== null ? label : 'No budget set'}
              </Typography>
            </Box>
            {pct !== null && monthlyBudget > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                  Spent
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {formatAmount(totalExpenses)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                  of {formatAmount(monthlyBudget)}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {pct !== null ? `${pct}%` : '—'}
            </Typography>
            {icon && <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>}
          </Box>
          <LinearProgress
            variant="determinate"
            value={displayPct}
            sx={{
              height: 6,
              borderRadius: 3,
              mb: 0.5,
              backgroundColor: 'action.hover',
              '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 3 },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {pct !== null ? label : 'No budget set'}
          </Typography>
        </>
      )}
    </WidgetCard>
  );
};

// ── 3. Upcoming Bills ─────────────────────────────────────────────────────────
const UpcomingBillsWidget = ({ pinned, onTogglePin, mobileVariant }) => {
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/payables/month')
      .then((res) => {
        if (!cancelled) setMonthData(res.data?.data || res.data);
      })
      .catch(() => {
        if (!cancelled) setMonthData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const { count, nextDate, remaining, coverage } = useMemo(() => {
    const items = monthData?.items || [];
    const unpaid = items.filter((i) => i.status !== 'paid');
    unpaid.sort((a, b) => new Date(a.dueOn) - new Date(b.dueOn));
    const first = unpaid[0];
    let nextDate = null;
    if (first?.dueOn) {
      const d = new Date(first.dueOn);
      if (!isNaN(d.getTime())) {
        nextDate = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
    }
    return {
      count: unpaid.length,
      nextDate,
      remaining: monthData?.totals?.remaining || 0,
      coverage: monthData?.coverage?.status || 'green',
    };
  }, [monthData]);

  const isCompact = mobileVariant === 'compact';
  const color = coverage === 'red' ? '#ef4444' : coverage === 'amber' ? '#f59e0b' : '#3b82f6';

  return (
    <WidgetCard
      id="upcoming_bills"
      title="Payables"
      icon={<EventRepeatIcon sx={{ fontSize: 18 }} />}
      color={color}
      pinned={pinned}
      onTogglePin={onTogglePin}
      loading={loading}
      mobileVariant={mobileVariant}
    >
      <Box
        onClick={() => navigate('/dashboard/payables')}
        sx={{ display: 'block', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            variant={isCompact ? 'h4' : 'h5'}
            fontWeight={800}
            sx={{
              lineHeight: 1.1,
              color: count > 0 ? color : 'text.primary',
              fontSize: isCompact ? '1.9rem' : undefined,
              letterSpacing: '-0.02em',
            }}
          >
            {count}
          </Typography>
          {isCompact && (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              due
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {count === 0
            ? 'None this month'
            : `${formatAmount(remaining)} left${nextDate ? ` · next ${nextDate}` : ''}`}
        </Typography>
      </Box>
    </WidgetCard>
  );
};

// ── 4. Savings Progress ───────────────────────────────────────────────────────
const SavingsProgressWidget = ({ pinned, onTogglePin, mobileVariant }) => {
  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(true);
  const { formatAmount } = useCurrency();

  useEffect(() => {
    let cancelled = false;
    setGoalsLoading(true);
    api
      .get('/goals')
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.goals || [];
        setGoals(list);
      })
      .catch(() => {
        if (!cancelled) setGoals([]);
      })
      .finally(() => {
        if (!cancelled) setGoalsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const avgPct = useMemo(() => {
    const active = goals.filter((g) => g.targetAmount > 0);
    if (active.length === 0) return null;
    const total = active.reduce((sum, g) => {
      const pct = Math.min((Number(g.currentAmount || 0) / Number(g.targetAmount)) * 100, 100);
      return sum + pct;
    }, 0);
    return Math.round(total / active.length);
  }, [goals]);

  const totalSaved = useMemo(
    () => goals.reduce((sum, g) => sum + Number(g.currentAmount || 0), 0),
    [goals]
  );

  const isWide = mobileVariant === 'wide';

  return (
    <WidgetCard
      id="savings_progress"
      title="Savings"
      icon={<SavingsIcon sx={{ fontSize: 18 }} />}
      color="#10b981"
      pinned={pinned}
      onTogglePin={onTogglePin}
      loading={goalsLoading}
      mobileVariant={mobileVariant}
    >
      {isWide ? (
        // ── Wide horizontal variant: amount left, % right, progress bar full-width ──
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.1, color: '#10b981', letterSpacing: '-0.02em' }}>
                {avgPct !== null ? formatAmount(totalSaved) : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {avgPct !== null
                  ? `across ${goals.length} goal${goals.length === 1 ? '' : 's'}`
                  : 'No savings goals yet'}
              </Typography>
            </Box>
            {avgPct !== null && (
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981', lineHeight: 1, letterSpacing: '-0.02em' }}>
                  {avgPct}%
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  avg
                </Typography>
              </Box>
            )}
          </Box>
          {avgPct !== null && (
            <LinearProgress
              variant="determinate"
              value={avgPct}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(16,185,129,0.12)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                  borderRadius: 4,
                },
              }}
            />
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, color: '#10b981' }}>
              {avgPct !== null ? `${avgPct}%` : '—'}
            </Typography>
            {goals.length > 0 && (
              <Chip
                label={`${goals.length} goal${goals.length === 1 ? '' : 's'}`}
                size="small"
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            )}
          </Box>
          {avgPct !== null && (
            <LinearProgress
              variant="determinate"
              value={avgPct}
              sx={{
                height: 6,
                borderRadius: 3,
                mb: 0.5,
                backgroundColor: 'action.hover',
                '& .MuiLinearProgress-bar': { backgroundColor: '#10b981', borderRadius: 3 },
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {avgPct !== null
              ? `${formatAmount(totalSaved)} saved across all goals`
              : 'No savings goals yet'}
          </Typography>
        </>
      )}
    </WidgetCard>
  );
};

// ─── Widget registry ──────────────────────────────────────────────────────────

const WIDGET_COMPONENTS = {
  spending_today: SpendingTodayWidget,
  budget_health: BudgetHealthWidget,
  upcoming_bills: UpcomingBillsWidget,
  savings_progress: SavingsProgressWidget,
};

const WIDGET_LABELS = {
  spending_today: 'Spending Today',
  budget_health: 'Budget Health',
  upcoming_bills: 'Upcoming Bills',
  savings_progress: 'Savings Progress',
};

// ─── Mobile bento layout config ──────────────────────────────────────────────
// Each pinned widget declares its visual slot on mobile.
// Browser auto-flow places them; spans create the asymmetric bento grid.
const MOBILE_LAYOUT = {
  budget_health:    { variant: 'hero',    span: 2 },
  spending_today:   { variant: 'compact', span: 1 },
  upcoming_bills:   { variant: 'compact', span: 1 },
  savings_progress: { variant: 'wide',    span: 2 },
};

// ─── Pin persistence ──────────────────────────────────────────────────────────

const loadPinned = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PINNED;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return DEFAULT_PINNED;
  } catch {
    return DEFAULT_PINNED;
  }
};

const savePinned = (pinned) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pinned));
  } catch {
    // localStorage unavailable — silently skip
  }
};

// ─── Main DashboardWidgets Component ─────────────────────────────────────────

/**
 * DashboardWidgets
 *
 * Props:
 *   sx {object} — optional MUI sx overrides for the outer Box wrapper
 */
const DashboardWidgets = ({ sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pinned, setPinned] = useState(loadPinned);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  // Persist whenever pinned changes
  useEffect(() => {
    savePinned(pinned);
  }, [pinned]);

  const togglePin = useCallback((id) => {
    setPinned((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  }, []);

  const pinnedWidgets = ALL_WIDGET_IDS.filter((id) => pinned.includes(id));

  return (
    <Box sx={{ mb: 3, ...sx }}>
      {/* Section header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontSize: '0.7rem',
          }}
        >
          At a Glance
        </Typography>
        <Button
          size="small"
          startIcon={<TuneIcon sx={{ fontSize: 14 }} />}
          onClick={() => setCustomizeOpen((o) => !o)}
          sx={{ fontSize: '0.7rem', textTransform: 'none', opacity: 0.75 }}
        >
          Customize
        </Button>
      </Box>

      {/* Customize panel */}
      <Collapse in={customizeOpen} timeout={200}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            background: 'transparent',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
            Toggle widgets to show/hide them on your dashboard:
          </Typography>
          {ALL_WIDGET_IDS.map((id) => {
            const isPinned = pinned.includes(id);
            return (
              <Chip
                key={id}
                label={WIDGET_LABELS[id]}
                icon={isPinned ? <PushPinIcon sx={{ fontSize: '14px !important' }} /> : <PushPinOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                onClick={() => togglePin(id)}
                variant={isPinned ? 'filled' : 'outlined'}
                color={isPinned ? 'primary' : 'default'}
                size="small"
                sx={{ cursor: 'pointer', fontWeight: isPinned ? 600 : 400 }}
              />
            );
          })}
        </Box>
      </Collapse>

      {/* Widget layout */}
      {pinnedWidgets.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No widgets pinned.{' '}
          <Button
            size="small"
            onClick={() => setPinned(DEFAULT_PINNED)}
            sx={{ textTransform: 'none', p: 0, minWidth: 'auto', fontWeight: 600 }}
          >
            Restore defaults
          </Button>
        </Typography>
      ) : isMobile ? (
        // ── Mobile: asymmetric bento grid ────────────────────────────────
        // Hero (Budget Health) spans full width with circular gauge.
        // Compact tiles (Spending Today + Upcoming Bills) sit side-by-side.
        // Wide tile (Savings Progress) spans full width with horizontal layout.
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1.5,
            gridAutoFlow: 'row dense',
          }}
        >
          {pinnedWidgets.map((id) => {
            const WidgetComponent = WIDGET_COMPONENTS[id];
            if (!WidgetComponent) return null;
            const layout = MOBILE_LAYOUT[id] || { variant: 'compact', span: 1 };
            return (
              <Box key={id} sx={{ gridColumn: `span ${layout.span}`, display: 'flex' }}>
                <WidgetComponent
                  pinned={pinned.includes(id)}
                  onTogglePin={togglePin}
                  mobileVariant={layout.variant}
                />
              </Box>
            );
          })}
        </Box>
      ) : (
        // ── Tablet / Desktop: responsive grid ───────────────────────────
        <Grid container spacing={2}>
          {pinnedWidgets.map((id) => {
            const WidgetComponent = WIDGET_COMPONENTS[id];
            if (!WidgetComponent) return null;
            return (
              <Grid item xs={6} sm={6} md={3} key={id}>
                <WidgetComponent
                  pinned={pinned.includes(id)}
                  onTogglePin={togglePin}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default DashboardWidgets;
